/**
 * Application state management.
 * Holds the in-memory editing buffer and data factory functions. The buffer is
 * not persisted anywhere: the edit session (`@/platform/editor-sync-context`)
 * is what writes it and sends it to the platform.
 * @module state/store
 */

import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

import type {
  AwardStandard,
  Programme,
  ProgrammeVersion,
  ProviderContact,
  ProviderSettings,
  Stage,
} from "@/types";
import { isFeAwardType } from "@/utils/award-types";
import {
  evaluateDevelopmentSetup,
  identitySetupComplete,
  isCasSelectionComplete,
  stepGate,
} from "@/utils/development-setup";
import { findVersion } from "@/utils/helpers";
import { SCHEMA_VERSION } from "@/utils/migrate-programme";
import { getStepCompletion } from "@/utils/step-completion";
import { uid } from "@/utils/uid";
import { validateProgramme, type ValidationFlag } from "@/utils/validation";

// Data-entry workflow steps, in the order the five surfaces list them.
// Review/output views live in `viewsOutputs`, in the same surface order.
export const steps: readonly { key: string; title: string }[] = [
  { key: "identity", title: "Identity" },
  { key: "cas-picker", title: "CAS components" },
  { key: "outcomes", title: "Programme outcomes" },
  { key: "versions", title: "Programme versions" },
  { key: "structure", title: "Modules and electives" },
  { key: "electives", title: "Electives" },
  { key: "stages", title: "Programme structure" },
  { key: "award-pathway", title: "Award pathway" },
  { key: "mimlos", title: "Module outcomes" },
  { key: "effort-hours", title: "Effort hours" },
  { key: "assessments", title: "Assessments" },
  { key: "reading-lists", title: "Reading lists" },
  { key: "mapping", title: "Mapping" },
  { key: "narrative", title: "Narrative" },
];

// Review/output views, grouped separately from the data-entry steps.
// These present or export the programme rather than capturing new data, so
// they live in their own Views/Outputs grouping in the navigation.
export const viewsOutputs: readonly { key: string; title: string }[] = [
  { key: "schedule", title: "Programme schedule" },
  { key: "module-view", title: "Module view" },
  { key: "traceability", title: "Traceability" },
  { key: "snapshot", title: "QQI snapshot" },
];

// Options
export const SCHOOL_OPTIONS: readonly string[] = [
  "Computing",
  "Business",
  "Psychology",
  "Education",
  "Engineering",
  "Science",
  "Health & Social Care",
  "Arts & Humanities",
  "Law",
  "Energy & Built Environment",
  "Tourism & Hospitality",
  "Culinary Arts",
  "Personal Services",
  "Other",
];

export const AWARD_TYPE_OPTIONS: readonly string[] = [
  "FE Major Award",
  "FE Component Award",
  "FE Minor Award",
  "FE Special Purpose Award",
  "FE Supplemental Award",
  "Higher Certificate",
  "Ordinary Bachelor Degree",
  "Honours Bachelor Degree",
  "Higher Diploma",
  "Postgraduate Diploma",
  "Masters",
  "Doctoral",
  "Micro-credential",
  "Other",
];

/**
 * Creates a neutral, institution-agnostic provider settings object.
 * All fields default to empty so no provider identity is implied until configured.
 */
export const defaultProviderContact = (): ProviderContact => ({
  name: "",
  title: "",
  address: "",
  email: "",
  phone: "",
  mobile: "",
});

export const defaultProviderSettings = (): ProviderSettings => ({
  name: "",
  abbreviation: "",
  contactEmail: "",
  website: "",
  legalName: "",
  address: "",
  contactName: "",
  qaProceduresUrl: "",
  validationContact: defaultProviderContact(),
  allowProgrammeLeadTeamManagement: true,
});

/**
 * Creates an empty programme object with default values.
 */
export const defaultProgramme = (): Programme => ({
  schemaVersion: SCHEMA_VERSION,
  id: "current",

  // Identity
  title: "",
  awardType: "",
  awardTypeIsOther: false,
  nfqLevel: null,
  discipline: "",
  awardStandardIds: [],
  awardStandardNames: [],

  // Provider/tenant settings (institution-neutral by default)
  providerSettings: defaultProviderSettings(),
  heDescriptor: {},

  // Programme-level structure
  totalCredits: 0,
  feCredits: 0,
  modules: [],
  plos: [],
  ploToMimlos: {},
  embeddedAwards: [],

  // Elective definitions
  electiveDefinitions: [],

  // Versions
  versions: [],

  updatedAt: null,
});

/**
 * Creates a new programme version with default values.
 */
export const defaultVersion = (): ProgrammeVersion => ({
  id: uid("ver"),
  label: "Full-time",
  code: "FT",
  duration: "",
  intakes: [],
  targetCohortSize: 0,
  numberOfGroups: 0,

  deliveryModality: "F2F",
  deliveryPatterns: {},
  deliveryNotes: "",

  onlineProctoredExams: "TBC",
  onlineProctoredExamsNotes: "",

  stages: [],
});

/**
 * Creates a new stage object with default values.
 */
export const defaultStage = (sequence = 1): Stage => ({
  id: uid("stage"),
  name: `Stage ${sequence}`,
  sequence,
  academicStage: sequence,
  creditsTarget: 0,
  teachingWeeks: 12,
  modules: [],
  electiveChoiceSlots: [],
});

/** A partial programme to merge, or an Immer recipe over the programme. */
export type ProgrammeUpdate = Partial<Programme> | ((draft: Programme) => void);

/** The editor's selection: which version and module every step shows. */
export interface EditorSelection {
  selectedVersionId: string | null;
  selectedModuleId: string | null;
}

/** The editing buffer's fields. */
export interface EditorFields extends EditorSelection {
  programme: Programme;
  stepIndex: number;
  lastSaved: string | null;
  reportTypeId?: string;
  reportVersionId?: string;
}

export interface EditorState extends EditorFields {
  setSelection: (selection: Partial<EditorSelection>) => void;
  setStepIndex: (stepIndex: number) => void;
}

/**
 * The editor store. Every write produces a new, frozen programme, so a
 * subscriber sees a change as a new identity. Never save from a store
 * subscription: a store change is every keystroke, and a save is the edit
 * session's to decide.
 */
export const useEditorStore = create<EditorState>()(
  immer((set) => ({
    programme: defaultProgramme(),
    stepIndex: 0,
    lastSaved: null,
    selectedVersionId: null,
    selectedModuleId: null,
    setSelection: (selection) => set(selection),
    setStepIndex: (stepIndex) => set({ stepIndex }),
  })),
);

function editorField<K extends keyof EditorFields>(key: K): PropertyDescriptor {
  return {
    enumerable: true,
    get: () => useEditorStore.getState()[key],
    set: (value: EditorFields[K]) => useEditorStore.setState({ [key]: value }),
  };
}

/**
 * The editor store's fields as plain properties, for code outside React.
 * Assigning one sets it in the store.
 */
export const state = Object.defineProperties({} as EditorFields, {
  programme: editorField("programme"),
  stepIndex: editorField("stepIndex"),
  lastSaved: editorField("lastSaved"),
  selectedVersionId: editorField("selectedVersionId"),
  selectedModuleId: editorField("selectedModuleId"),
  reportTypeId: editorField("reportTypeId"),
  reportVersionId: editorField("reportVersionId"),
});

/**
 * A workflow step resolved for the current programme, including whether it is
 * locked. Identity gates the workflow: downstream steps are `locked` until the
 * Identity step has no blocking errors.
 */
export interface ActiveStep {
  key: string;
  title: string;
  locked: boolean;
}

/**
 * Whether the given validation flags contain a blocking Identity error.
 */
function hasIdentityErrors(flags: ValidationFlag[]): boolean {
  return flags.some((flag) => flag.step === "identity" && flag.type === "error");
}

/**
 * Whether the programme's Identity step is complete enough to assess the rest
 * of the workflow. Identity gates every other step: until its required fields
 * are valid (no blocking errors), downstream steps are locked and their
 * statuses are not meaningful.
 */
export function isIdentityComplete(programme: Programme = state.programme): boolean {
  // Setup's own test as well: validation only warns on a missing award type
  // and files zero credits under structure.
  return (
    identitySetupComplete(programme) &&
    !hasIdentityErrors(validateProgramme(programme) as ValidationFlag[])
  );
}

/**
 * Whether a step is locked: the identity step is always unlocked, and every
 * other step waits on what its gate names.
 */
function isStepLocked(
  stepKey: string,
  programme: Programme,
  identityComplete: boolean,
  setupComplete: boolean,
): boolean {
  if (stepKey === "identity") {
    return false;
  }
  switch (stepGate(programme, stepKey)) {
    case "identity":
      return !identityComplete;
    case "cas-components":
      return !identityComplete || !isCasSelectionComplete(programme);
    default:
      // Setup counts Identity's fields, not whether they are valid.
      return !identityComplete || !setupComplete;
  }
}

export type StepLockGate = "identity" | "cas-components" | "setup";

/**
 * Which precondition currently locks a step, or `null` when it isn't locked:
 * Identity, a CAS programme's components, or the whole development-setup
 * group. A step behind the CAS components names Identity while Identity is
 * the thing outstanding.
 */
export function stepLockGate(
  stepKey: string,
  programme: Programme = state.programme,
  // Passed in by a caller explaining a list of locked rows: the check reads
  // the whole programme, and one gate is the same for every row in the pass.
  knownIdentityComplete?: boolean,
): StepLockGate | null {
  if (programme.mode === "MODULE_EDITOR") {
    return null;
  }
  const identityComplete = knownIdentityComplete ?? isIdentityComplete(programme);
  const setupComplete = evaluateDevelopmentSetup(programme).complete;
  if (!isStepLocked(stepKey, programme, identityComplete, setupComplete)) {
    return null;
  }
  const gate = stepGate(programme, stepKey);
  return gate === "cas-components" && !identityComplete ? "identity" : gate;
}

/**
 * Returns the active workflow steps based on the current programme mode.
 *
 * In MODULE_EDITOR mode, only module-related steps are shown (and never
 * locked, since Identity is not part of that flow). Otherwise the Identity
 * step is always accessible and every downstream step is `locked` until
 * Identity is complete (no blocking errors), so callers can block navigation
 * or present a read-only preview for gated steps.
 */
export function activeSteps(p: Programme = state.programme): ActiveStep[] {
  if (p.mode === "MODULE_EDITOR") {
    const allowed = new Set(["mimlos", "effort-hours", "assessments", "reading-lists", "mapping"]);
    return steps.filter((s) => allowed.has(s.key)).map((s) => ({ ...s, locked: false }));
  }

  const isFe = isFeAwardType(p.awardType ?? "");
  const identityComplete = isIdentityComplete(p);
  const setupComplete = evaluateDevelopmentSetup(p).complete;
  // No FE-specific order any more: `steps` is held in authoring order for
  // every route, which is the order this sort used to impose on one of them.
  return [...steps]
    .filter((s) => (s.key === "cas-picker" ? isFe : true))
    .map((s) => ({
      ...s,
      locked: isStepLocked(s.key, p, identityComplete, setupComplete),
    }));
}

/**
 * Returns the active review/output views (Module View, Programme Schedule,
 * Traceability, QQI Snapshot) for the current programme mode.
 *
 * Like {@link activeSteps}, views are gated behind a complete Identity step
 * (locked until Identity has no blocking errors), so callers can block
 * navigation or present a read-only preview. In MODULE_EDITOR mode only the
 * module-relevant views are returned and they are never locked.
 */
export function activeViewsOutputs(p: Programme = state.programme): ActiveStep[] {
  if (p.mode === "MODULE_EDITOR") {
    const allowed = new Set(["schedule", "traceability", "snapshot"]);
    return viewsOutputs.filter((v) => allowed.has(v.key)).map((v) => ({ ...v, locked: false }));
  }

  const locked = !isIdentityComplete(p) || !evaluateDevelopmentSetup(p).complete;
  return viewsOutputs.map((v) => ({ ...v, locked }));
}

/**
 * Returns the IDs of modules that can be edited in the current mode.
 * In MODULE_EDITOR mode, returns only assigned modules.
 */
export function editableModuleIds(p: Programme = state.programme): string[] {
  if (!p) {
    return [];
  }
  if (p.mode === "MODULE_EDITOR") {
    const ids = p.moduleEditor?.assignedModuleIds ?? [];
    return ids.length ? ids : (p.modules ?? []).map((m) => m.id);
  }
  return (p.modules ?? []).map((m) => m.id);
}

/**
 * Returns the currently selected module ID for the module picker.
 * Auto-selects the first editable module if none selected or selection is invalid.
 */
export function getSelectedModuleId(): string {
  const ids = editableModuleIds();
  if (!ids.length) {
    return "";
  }
  const { selectedModuleId, setSelection } = useEditorStore.getState();
  if (!selectedModuleId || !ids.includes(selectedModuleId)) {
    setSelection({ selectedModuleId: ids[0] });
    return ids[0];
  }
  return selectedModuleId;
}

/**
 * Finds a programme version by its ID.
 */
export function getVersionById(id: string): ProgrammeVersion | undefined {
  return findVersion(state.programme.versions ?? [], id);
}

/**
 * Human-readable status badge for a workflow step, derived live from programme data.
 */
export type StepStatus = "Not started" | "Complete" | "Attention" | "Review";

/**
 * Derives the status badge for a workflow step from the current programme.
 *
 * Reads the live store programme by default so the result always reflects the
 * latest data mutation — no manual refresh is needed. The Sidebar wires badge
 * derivation to the store's re-render/save path so these statuses recompute
 * immediately after any change.
 *
 * On a blank/new programme every step stays a neutral "Not started" until the
 * Identity step is complete, so downstream steps don't surface misleading
 * statuses before the programme is assessable.
 */
export function getStepStatus(
  stepKey: string,
  programme: Programme = state.programme,
  // A caller asking about many steps validates once and passes the flags in.
  flags: ValidationFlag[] = validateProgramme(programme) as ValidationFlag[],
): StepStatus {
  if (hasIdentityErrors(flags)) {
    return "Not started";
  }

  let hasError = false;
  let hasWarn = false;
  flags.forEach((flag) => {
    if (flag.step === stepKey) {
      if (flag.type === "error") {
        hasError = true;
      } else {
        hasWarn = true;
      }
    }
  });
  if (hasError) {
    return "Attention";
  }
  const completion = getStepCompletion(stepKey, programme);
  if (completion === "not-started") {
    return "Not started";
  }
  if (hasWarn) {
    return "Review";
  }
  if (completion === "partial") {
    return "Review";
  }
  return "Complete";
}

/**
 * The programme an imported file gives: a clean, migrated document over the
 * editor's defaults, so fields the file lacks are the defaults rather than
 * whatever the programme it replaces held.
 */
export function importedProgramme(migrated: object): Programme {
  return { ...defaultProgramme(), ...(migrated as Programme) };
}

/**
 * A recipe replacing the whole programme with `next`, as import and reset do,
 * keeping the open programme's id: the record it saves to is still that one,
 * and review locks and permissions look the programme up by it.
 */
export function replacingProgramme(next: Programme): (draft: Programme) => void {
  return (draft) => {
    const { id } = draft;
    const fields = draft as unknown as Record<string, unknown>;
    Object.keys(fields).forEach((key) => {
      delete fields[key];
    });
    Object.assign(fields, structuredClone(next), { id });
  };
}

/** Back to the first step with nothing selected, as a reset leaves the editor. */
export function resetEditorNavigation(): void {
  useEditorStore.setState({ stepIndex: 0, selectedVersionId: null, selectedModuleId: null });
}

/**
 * Stamps the document's `updatedAt`, and the save pill's last-sent time, as an
 * edit leaves the session's window. The export dates a descriptor from it.
 * The session calls it; nothing else does.
 */
export function stampSent(): void {
  const now = new Date().toISOString();
  useEditorStore.setState((draft) => {
    draft.programme.updatedAt = now;
    draft.lastSaved = now;
  });
}

/**
 * A recipe switching the programme between PROGRAMME_OWNER and MODULE_EDITOR.
 * MODULE_EDITOR restricts editing to the assigned modules and their steps.
 */
export function withMode(
  mode: "PROGRAMME_OWNER" | "MODULE_EDITOR",
  assignedModuleIds: string[] = [],
): (draft: Programme) => void {
  return (draft) => {
    draft.mode = mode;
    if (mode !== "MODULE_EDITOR") {
      delete draft.moduleEditor;
      return;
    }
    const defaultAssigned = (draft.modules ?? []).map((m) => m.id);
    draft.moduleEditor ??= { assignedModuleIds: [], locks: undefined };
    draft.moduleEditor.assignedModuleIds = assignedModuleIds.length
      ? assignedModuleIds
      : defaultAssigned;
    draft.moduleEditor.locks ??= {
      programme: true,
      modulesMeta: true,
      versions: true,
      plos: true,
    };
  };
}

/** Moves to a step the mode allows, when the one open is not. */
export function focusStepForMode(mode: "PROGRAMME_OWNER" | "MODULE_EDITOR"): void {
  const currentKey = steps[state.stepIndex]?.key;
  const allowed = new Set(["mimlos", "mapping", "assessments"]);
  if (mode === "MODULE_EDITOR" && !allowed.has(currentKey)) {
    const idx = steps.findIndex((s) => s.key === "mimlos");
    if (idx >= 0) {
      useEditorStore.getState().setStepIndex(idx);
    }
  }
}

// Cache for award standards
let standardsPromise: Promise<AwardStandard[]> | null = null;

/**
 * Loads and caches the standards.json file.
 */
async function loadStandardsFile(): Promise<AwardStandard[]> {
  if (standardsPromise) {
    return standardsPromise;
  }
  standardsPromise = (async () => {
    // Absolute, not relative: a relative "./assets/..." resolves against the
    // current page path, which is fine at "/" but wrong at a nested client
    // route like "/programmes/:id/edit/identity" (it would resolve under
    // that route instead of the site root).
    const baseUrl = import.meta.env.BASE_URL ?? "/";
    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    const res = await fetch(`${normalizedBaseUrl}assets/standards.json`, { cache: "no-store" });
    if (!res.ok) {
      throw new Error("Failed to load standards.json");
    }
    const data = await res.json();

    const extractStandardsArray = (raw: unknown): AwardStandard[] => {
      if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        const obj = raw as { awardStandards?: unknown; standards?: unknown };
        if (Array.isArray(obj.awardStandards)) {
          return obj.awardStandards as AwardStandard[];
        }
        if (Array.isArray(obj.standards)) {
          return obj.standards as AwardStandard[];
        }
      }
      if (Array.isArray(raw)) {
        return raw as AwardStandard[];
      }
      return [raw as AwardStandard];
    };

    // New format: { schemaVersion, generatedFrom, awardStandards: [...] }
    // FE and HE standards are both stored in the canonical standards.json.
    return extractStandardsArray(data);
  })().catch((error: unknown) => {
    // A rejected load must not stick around forever: clear the cache so a
    // later call (e.g. a user retry) fetches again instead of replaying the
    // same failure for the rest of the session.
    standardsPromise = null;
    throw error;
  });
  return standardsPromise;
}

/**
 * Returns all available award standards.
 */
export async function getAwardStandards(): Promise<AwardStandard[]> {
  return await loadStandardsFile();
}

/**
 * Returns a single award standard by ID.
 */
export async function getAwardStandard(standardId?: string): Promise<AwardStandard | null> {
  const list = await loadStandardsFile();
  if (!standardId) {
    return list[0] || null;
  }
  return list.find((s) => s && s.id === standardId) || list[0] || null;
}

// Export helper functions from migrate-programme for convenience
export {
  getCriteriaList,
  getDescriptor,
  getStandardIndicators,
  getThreadList,
  migrateProgramme,
  validateStandardMappings,
} from "@/utils/migrate-programme";
